import React, { useEffect } from 'react';

import {
  BrowserRouter, 
  Navigate, 
  Route, 
  Routes,
  useLocation
} from 'react-router-dom';

import Shell from './components/layout/Shell';
import ErrorState from './components/state/ErrorState';
import LoadingState from './components/state/LoadingState';
import { useAuth } from './contexts/AuthContext';
import AddCaller from './pages/AddCaller';
import AddCareManager from './pages/AddCareManager';
import AddClient from './pages/AddClient';
import AddSurvey from './pages/AddSurvey';
import AllEncounters from './pages/AllEncounters';
import CallerEncounters from './pages/CallerEncounters';
import ClientEncounters from './pages/ClientEncounters';
import Dashboard from './pages/Dashboard';
import EditCaller from './pages/EditCaller';
import EditCareManager from './pages/EditCareManager';
import EditClient from './pages/EditClient';
import EditSurvey from './pages/EditSurvey';
import EncounterReport from './pages/EncounterReport';
import Login from './pages/Login';
import ManageAccount from './pages/ManageAccount';
import ManageCallers from './pages/ManageCallers';
import ManageCareManagers from './pages/ManageCareManagers';
import ManageClients from './pages/ManageClients';
import ManageIntegrations from './pages/ManageIntegrations';
import ManageSurveys from './pages/ManageSurveys';
import RecurringJobs from './pages/RecurringJobs';
import Register from './pages/Register';
import Settings from './pages/Settings';
import ViewVisits from './pages/ViewVisits';

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<
  React.PropsWithChildren<object>,
  ErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren<object>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorState
          message={this.state.error?.message || 'An unexpected error occurred. Please refresh the page.'}
          style={{ height: '100vh' }}
          title="Application Error"
        />
      );
    }

    return this.props.children;
  }
}

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  title?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, title }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  useEffect(() => {
    document.title = title || 'Phono';
  }, [location.pathname, title]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <LoadingState 
        message="Checking authentication..." 
        style={{ height: '100vh' }} 
      />
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate replace to="/login" />;
  }

  // Render protected content wrapped in Shell
  return <Shell>{children}</Shell>;
};

// Unprotected Route Component (for login, register, etc.)
interface UnprotectedRouteProps {
  children: React.ReactNode;
}

const UnprotectedRoute: React.FC<UnprotectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <LoadingState
        message="Checking authentication..." 
        style={{ height: '100vh' }} 
      />
    );
  }

  // Redirect to home if already authenticated
  if (isAuthenticated) {
    return <Navigate replace to="/" />;
  }

  // Render unprotected content
  return <>{children}</>;
};

// Main Router Component
const AppRouter: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Unprotected routes */}
          <Route 
            element={
              <UnprotectedRoute>
                <Login />
              </UnprotectedRoute>
            }
            path="/login" 
          />
          <Route 
            element={
              <UnprotectedRoute>
                <Register />
              </UnprotectedRoute>
            }
            path="/register" 
          />
          
          {/* Protected routes */}
          <Route 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
            path="/" 
          />
          <Route 
            element={
              <ProtectedRoute title="User Settings">
                <Settings />
              </ProtectedRoute>
            }
            path="/settings" 
          />
          <Route 
            element={
              <ProtectedRoute title="Manage Account">
                <ManageAccount />
              </ProtectedRoute>
            }
            path="/account/manage" 
          />
          <Route 
            element={
              <ProtectedRoute title="Manage Integrations">
                <ManageIntegrations />
              </ProtectedRoute>
            }
            path="/account/integrations" 
          />
          <Route 
            element={
              <ProtectedRoute title="Manage Clients">
                <ManageClients />
              </ProtectedRoute>
            }
            path="/clients" 
          />
          <Route 
            element={
              <ProtectedRoute title="Manage Callers">
                <ManageCallers />
              </ProtectedRoute>
            }
            path="/callers" 
          />
          <Route
            element={
              <ProtectedRoute title="Manage Care Managers">
                <ManageCareManagers />
              </ProtectedRoute>
            }
            path="/care_managers"
          />
          <Route
            element={
              <ProtectedRoute title="Add Care Manager">
                <AddCareManager />
              </ProtectedRoute>
            }
            path="/care_manager/add"
          />
          <Route
            element={
              <ProtectedRoute title="Edit Care Manager">
                <EditCareManager />
              </ProtectedRoute>
            }
            path="/care_manager/edit/:id"
          />
          <Route
            element={
              <ProtectedRoute title="Manage Surveys">
                <ManageSurveys />
              </ProtectedRoute>
            }
            path="/surveys"
          />
          <Route 
            element={
              <ProtectedRoute title="Add Survey">
                <AddSurvey />
              </ProtectedRoute>
            }
            path="/survey/add" 
          />
          <Route 
            element={
              <ProtectedRoute title="Edit Survey">
                <EditSurvey />
              </ProtectedRoute>
            }
            path="/survey/:id/edit" 
          />
          <Route 
            element={
              <ProtectedRoute title="Add Caller">
                <AddCaller />
              </ProtectedRoute>
            }
            path="/caller/add" 
          />
          <Route 
            element={
              <ProtectedRoute title="Edit Caller">
                <EditCaller />
              </ProtectedRoute>
            }
            path="/caller/edit/:id" 
          />
          <Route
            element={
              <ProtectedRoute title="Caller Encounters">
                <CallerEncounters />
              </ProtectedRoute>
            }
            path="/caller/:id/encounters"
          />
          <Route
            element={
              <ProtectedRoute title="Add Client">
                <AddClient />
              </ProtectedRoute>
            }
            path="/client/add"
          />
          <Route 
            element={
              <ProtectedRoute title="Edit Client">
                <EditClient />
              </ProtectedRoute>
            }
            path="/client/edit/:id" 
          />
          <Route 
            element={
              <ProtectedRoute title="Client Encounters">
                <ClientEncounters />
              </ProtectedRoute>
            }
            path="/client/:id/encounters" 
          />
          <Route
            element={
              <ProtectedRoute title="All Encounters">
                <AllEncounters />
              </ProtectedRoute>
            }
            path="/encounters"
          />
          <Route
            element={
              <ProtectedRoute title="Encounter Report">
                <EncounterReport />
              </ProtectedRoute>
            }
            path="/encounter/report"
          />
          <Route 
            element={
              <ProtectedRoute title="Visits">
                <ViewVisits />
              </ProtectedRoute>
            }
            path="/visits" 
          />
          <Route 
            element={
              <ProtectedRoute title="Recurring Jobs">
                <RecurringJobs />
              </ProtectedRoute>
            }
            path="/_recurring" 
          />
          
          {/* Catch all route - redirect to home */}
          <Route 
            element={<Navigate replace to="/" />}
            path="*" 
          />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default AppRouter;
